import { useCallback } from "react";
import { Country, Currency } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Product } from "../../../domain/entities/product/Product";
import { financial } from "../shared/util/financial/financial";
import { BasketItemAddedTrackingEvent, BasketItemRemovedTrackingEvent, PROJECT, TrackingEventName } from "./tracking";

interface TrackBasketItemFunctionArgs {
  readonly product: Product;
  readonly currency: Currency;
}

interface TrackBasketItemFunction {
  (args: TrackBasketItemFunctionArgs): void;
}

interface UseTrackBasketItemFunctionArgs {
  readonly event: TrackingEventName.BASKET_ITEM_ADDED | TrackingEventName.BASKET_ITEM_REMOVED;
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackBasketItemFunction {
  (agrs: UseTrackBasketItemFunctionArgs): TrackBasketItemFunction;
}

const useTrackBasketItem: UseTrackBasketItemFunction = ({ event, country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<BasketItemAddedTrackingEvent | BasketItemRemovedTrackingEvent>();

  const trackBasketItem: TrackBasketItemFunction = useCallback(
    ({ product, currency }) => {
      emitUserEvent({
        event,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        ecommerce: {
          add: {
            products: [
              {
                brand: product.brand,
                id: product.variantId,
                name: product.title,
                price: financial(product.price.amount),
                variant: product.size,
              },
            ],
          },
          currencyCode: currency,
        },
        items: [{ item_id: product._variantId }],
      });
    },
    [country, emitUserEvent, event, segment, tradename, userId],
  );

  return trackBasketItem;
};

export { useTrackBasketItem };
